import { useTranslation } from 'react-i18next'
import { useConsentStore } from '@/consent/consentStore'

/**
 * CookieBanner — CNIL-compliant cookie consent banner.
 *
 * Compliance invariants (Plan B / cookie compliance):
 * - 3 actions (Accept all / Reject all / Customize) have **equal visual weight**:
 *   same height (h-12), same width on md+ (md:flex-1). Do not change.
 * - Reject must be as easy as Accept (one click, same prominence).
 * - "Customize" leads to settings, not a dark-pattern hidden flow.
 */
export function CookieBanner() {
  const { t } = useTranslation('consent')
  const acceptAll = useConsentStore((s) => s.acceptAll)
  const rejectAll = useConsentStore((s) => s.rejectAll)
  const hideBanner = useConsentStore((s) => s.hideBanner)

  return (
    <div
      role="region"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-surface-raised shadow-lg md:bottom-6 md:right-6 md:left-auto md:max-w-[480px] md:rounded-xl md:border"
    >
      <div className="p-5 md:p-6">
        <h2 id="cookie-banner-title" className="text-lg font-semibold text-text-primary md:text-xl">
          {t('banner.title')}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-text-secondary md:text-base">
          {t('banner.description')}
        </p>

        <div className="mt-5 flex flex-col gap-2 md:flex-row md:gap-3">
          <button
            type="button"
            onClick={() => {
              void acceptAll('banner')
            }}
            className="h-12 rounded-lg bg-action-primary-bg px-4 text-sm font-medium text-text-on-brand transition hover:bg-action-primary-bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus md:flex-1"
          >
            {t('banner.acceptAll')}
          </button>
          <button
            type="button"
            onClick={() => {
              void rejectAll('banner')
            }}
            className="h-12 rounded-lg border-[1.5px] border-action-secondary-border bg-action-secondary-bg px-4 text-sm font-medium text-text-primary transition hover:bg-action-secondary-bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus md:flex-1"
          >
            {t('banner.rejectAll')}
          </button>
          <a
            href="/account/privacy/cookies"
            onClick={() => hideBanner()}
            className="flex h-12 items-center justify-center rounded-lg bg-surface-subtle px-4 text-sm font-medium text-text-primary transition hover:bg-action-ghost-bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus md:flex-1"
          >
            {t('banner.customize')}
          </a>
        </div>

        <p className="mt-4 text-xs text-text-secondary">
          <a href="/legal/cookie" className="text-text-link underline-offset-2 hover:underline">
            {t('banner.details')}
          </a>
        </p>
      </div>
    </div>
  )
}
